// import DST from '@/utilities/DST.js';
import cloneDeep from 'lodash/cloneDeep.js';
import get from 'lodash/get.js';
import isEmpty from 'lodash/isEmpty.js';
import isNil from 'lodash/isNil.js';
import merge from 'lodash/merge.js';

import moment from '@/plugins/vue-moment.js';
import { DialogService } from '@/services/DialogService.js';
import Item from '@/store/quill/Item.js';

const getSettings = type => (type.includes('temp') ? 'activity_settings_template' : 'activity_settings');

const getNamespace = type => (type.includes('temp') ? 'ActivityTemplates' : 'Activities');

export default merge(
    Item({
        dates: ['created_at', 'updated_at'],
    }),
    {
        state() {
            return {
                activity_content: {
                    content: '',
                },
                activity_type: null,
                activity_type_key: null,
                activity_settings_template: {},
                activity_settings: {},
                created_at: null,
                completed: null,
                course_id: null,
                course_template_id: null,
                contentable_id: null,
                description: null,
                displayDescription: null,
                displayPrerequisite: false,
                displayDueDates: false,
                displayRubric: false,
                displayStatus: {
                    editing: true,
                },
                grading_scheme: null,
                gradebook: null,
                id: null,
                marked_completed_at: null,
                media: [],
                order: null,
                parent: null,
                points_possible: null,
                prerequisite: null,
                prerequisite_id: null,
                prerequisite_type: null,
                prerequisite_uuid: null,
                inverse_prerequisites: [],
                rubric: null,
                rubric_template: null,
                rubric_uuid: null,
                section_id: null,
                section_template_id: null,
                softCompleted: null,
                submissions: null,
                submission_count: null,
                user_submission_count: null,
                type: null,
                title: null,
                ungraded_submission_count: null,
                uuid: null,
                updated_at: null,
                remediations: null,
                question_count: null,

                is_generating: null,
                generation_status: null,
                generation_error: null,

                updateKey: null,
            };
        },

        getters: {
            getContent: state => field => get(state.activity_content, field),

            isLate: state => now => {
                return now.isAfter(moment.utc(state.activity_settings.due_at));
            },

            gradable: state => {
                const settings = state[getSettings(state.type)];
                const points_possible = get(settings, 'points_possible');
                const rubric_type = state.type.includes('temp') ? 'rubric_template' : 'rubric';

                if (state.activity_type === 'quiz') return true;

                if (['file-submission', 'discussion-thread', 'survey'].includes(state.activity_type)) {
                    return !isNil(points_possible) || !isEmpty(state[rubric_type]);
                }

                return !isNil(points_possible);
            },
        },

        actions: {
            assignRelationships(context, { item }) {
                if (!item.parent) {
                    context.commit('EDIT_FIELD', {
                        field: 'parent',
                        value: null,
                    });
                } else {
                    context.commit('EDIT_FIELD', {
                        field: 'parent',
                        value: item.parent,
                    });
                }

                context.commit('EDIT_FIELD', {
                    field: 'order',
                    value: item.order,
                });
            },

            async applyTimeSettings({ rootGetters }, { settings, key, parent }) {
                const timezone = rootGetters['InstitutionSettings/get']('timezone');

                // Fallback to UTC if timezone not available
                const date = timezone ? moment.utc(settings[key]).tz(timezone) : moment.utc(settings[key]);
                const day = date.day();
                const hours = date.hour();
                const minutes = date.minute();
                const seconds = date.second();

                const start = timezone ? moment.utc(parent.week.start).tz(timezone) : moment.utc(parent.week.start);
                const end = timezone ? moment.utc(parent.week.end).tz(timezone) : moment.utc(parent.week.end);

                const startPoint = day === 0 ? end : start;

                // App is accounting for DST when setting initial week sections. Since DST has been accounted
                // for then, it is no longer required to account for it below. In testing accounting for DST
                // in the activity it did not function how it originally did.

                // const dstHelper = new DST(timezone);
                //
                // const adjustForDST = dstHelper.checkForDST({
                //     reference: startPoint,
                //     target: date,
                // });
                //
                // if (adjustForDST === 'add') hours += 1;
                //
                // if (adjustForDST === 'minus') hours -= 1;

                return startPoint.day(day).hour(hours).minute(minutes).second(seconds).toDate();
            },

            quizContent(context, { field, value }) {
                const namespace = getNamespace(context.state.type);

                context.commit('EDIT_CONTENT', { field, value });

                context.commit(
                    `${namespace}/QUIZ_CONTENT`,
                    {
                        target: context.state.uuid,
                        field,
                        value,
                    },
                    { root: true }
                );
            },

            async parseSettings({ commit, dispatch, rootGetters, state }) {
                const namespace = getNamespace(state.type);
                const template = state.type.includes('temp');

                if (template && state.parent) {
                    const template_id = rootGetters[`SectionTemplates/${state.parent}/get`]('id');

                    commit('EDIT_FIELD', {
                        field: 'section_template_id',
                        value: template_id,
                    });
                } else if (template && !state.parent) {
                    commit('EDIT_FIELD', {
                        field: 'section_template_id',
                        value: null,
                    });
                }

                if (!template && state.parent) {
                    const id = rootGetters[`Sections/${state.parent}/get`]('id');

                    commit('EDIT_FIELD', { field: 'section_id', value: id });
                } else if (!template && !state.parent) {
                    commit('EDIT_FIELD', { field: 'section_id', value: null });
                }

                const parent = rootGetters[`${namespace}/getWeekStatus`]({
                    uuid: state.uuid,
                    template,
                });

                if (template) {
                    if (parent.type === 'week') {
                        commit('EDIT_SETTINGS', {
                            field: 'week_due',
                            value: parent.week,
                        });

                        commit('EDIT_SETTINGS', {
                            field: 'week_visible',
                            value: parent.week,
                        });
                    }
                }

                if (!template) {
                    if (parent.type === 'week') {
                        const settings = state.activity_settings;

                        if (settings.due_at) {
                            const dueAt = await dispatch('applyTimeSettings', {
                                settings,
                                key: 'due_at',
                                parent,
                            });

                            commit('EDIT_SETTINGS', {
                                field: 'due_at',
                                value: dueAt,
                            });
                        }

                        if (settings.visible_at) {
                            const visibleAt = await dispatch('applyTimeSettings', {
                                settings,
                                key: 'visible_at',
                                parent,
                            });

                            commit('EDIT_SETTINGS', {
                                field: 'visible_at',
                                value: visibleAt,
                            });
                        }
                    }
                }
            },

            removeDueDates({ dispatch }, { message, action }) {
                DialogService.confirm({
                    message,
                    title: 'Remove Dates',
                    type: 'is-danger',
                    hasIcon: false,
                    confirmText: 'Remove',
                    cancelText: 'Go Back',
                    canCancel: ['button'],
                    onConfirm: () => {
                        dispatch(action);
                    },
                });
            },

            resetDueAtSettings(context) {
                const temp = context.state.type.includes('temp');
                const settings = getSettings(context.state.type);
                const namespace = getNamespace(context.state.type);

                if (temp) {
                    context.commit('EDIT_SETTINGS', {
                        field: 'day_due',
                        value: null,
                    });

                    context.commit('EDIT_SETTINGS', {
                        field: 'time_due',
                        value: null,
                    });

                    // Trigger AutoSave

                    context.commit(
                        `${namespace}/SETTINGS`,
                        {
                            uuid: context.state.uuid,
                            type: settings,
                            field: 'allow_submissions_after_due_date',
                            value: false,
                        },
                        { root: true }
                    );
                }

                if (!temp) {
                    context.commit('EDIT_SETTINGS', {
                        field: 'due_at',
                        value: null,
                    });

                    // Trigger AutoSave

                    context.commit(
                        `${namespace}/SETTINGS`,
                        {
                            uuid: context.state.uuid,
                            type: settings,
                            field: 'allow_submissions_after_due_date',
                            value: false,
                        },
                        { root: true }
                    );
                }
            },

            resetVisibleAtSettings(context) {
                const temp = context.state.type.includes('temp');
                const settings = getSettings(context.state.type);
                const namespace = getNamespace(context.state.type);

                if (temp) {
                    context.commit('EDIT_SETTINGS', {
                        field: 'day_visible',
                        value: null,
                    });

                    context.commit('EDIT_SETTINGS', {
                        field: 'time_visible',
                        value: null,
                    });

                    // Trigger AutoSave

                    context.commit(
                        `${namespace}/SETTINGS`,
                        {
                            uuid: context.state.uuid,
                            type: settings,
                            field: 'remove_visible_at',
                            value: null,
                        },
                        { root: true }
                    );
                }

                if (!temp) {
                    // Trigger AutoSave

                    context.commit(
                        `${namespace}/SETTINGS`,
                        {
                            uuid: context.state.uuid,
                            type: settings,
                            field: 'visible_at',
                            value: null,
                        },
                        { root: true }
                    );
                }
            },

            removePrerequisite({ commit, state }) {
                commit('EDIT_FIELD', {
                    field: 'prerequisite',
                    value: null,
                });

                commit('EDIT_FIELD', {
                    field: 'prerequisite_id',
                    value: null,
                });

                commit('EDIT_FIELD', {
                    field: 'prerequisite_type',
                    value: null,
                });

                // Trigger auto save

                commit(
                    `${getNamespace(state.type)}/EDIT`,
                    {
                        uuid: state.uuid,
                        field: 'prerequisite_uuid',
                        value: null,
                    },
                    { root: true }
                );
            },

            setInversePrerequisites({ commit, state }, initiator) {
                const initiators = cloneDeep(state.inverse_prerequisites);

                if (!initiators.includes(initiator)) {
                    initiators.push(initiator);

                    commit('EDIT_FIELD', {
                        field: 'inverse_prerequisites',
                        value: initiators,
                    });
                }
            },

            removeInversePrerequisites({ commit, state }, target) {
                const initiators = cloneDeep(state.inverse_prerequisites);

                if (initiators.includes(target)) {
                    const index = initiators.findIndex(item => item === target);

                    initiators.splice(index, 1);

                    commit('EDIT_FIELD', {
                        field: 'inverse_prerequisites',
                        value: initiators,
                    });
                }
            },
        },

        mutations: {
            EDIT_FIELD(state, { field, value }) {
                state[field] = value;
            },

            EDIT_SETTINGS(state, { field, value }) {
                const settings = getSettings(state.type);

                state[settings][field] = value;
            },

            EDIT_CONTENT(state, { field, value }) {
                state.activity_content[field] = value;
            },
        },
    }
);
